import { ref } from "vue";
import { getStatuses } from "@/entities/workflow/api";
import i18n from "@/shared/config/i18n";

const DEFAULT_COLOR = "#6B7280";
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

// Fallback-палитра. Используется, если бэк /template-action-rules/statuses
// не вернул поле `color` для данного кода. Когда бэк начнёт отдавать `color` —
// его значение перекроет fallback автоматически (см. ensureStatusDictionary).
const FALLBACK_PALETTE = Object.freeze({
  // Notarial workflow
  PROJECT: "#9CA3AF",
  AWAITING_PAYMENT: "#EAB308",
  SIGNING: "#EAB308",
  SIGNED: "#16A34A",
  COMPLETED: "#16A34A",
  CANCELLED: "#EF4444",
  // Enforcement-specific
  PENDING_REVIEW: "#F97316",
  REGISTERED: "#3B82F6",
  ADDING_HEIRS: "#EAB308",
  CERTIFICATE_ISSUED: "#22C55E",
});

function safeHex(value) {
  if (typeof value !== "string") return null;
  return HEX_COLOR_RE.test(value.trim()) ? value.trim() : null;
}

const dict = ref(new Map());
let inflight = null;
let loadedLocale = null;
let warnedEmpty = false;

function currentLocale() {
  return i18n.global?.locale?.value || i18n.global?.locale || "ru";
}

export function getStatusDictionary() {
  return dict;
}

export function getStatusLabel(code) {
  if (!code) return "";
  const entry = dict.value.get(code);
  return entry?.label || code;
}

export function getStatusColor(code) {
  if (!code) return DEFAULT_COLOR;
  const entry = dict.value.get(code);
  return entry?.color || FALLBACK_PALETTE[code] || DEFAULT_COLOR;
}

export function resetStatusDictionary() {
  dict.value = new Map();
  inflight = null;
  loadedLocale = null;
  warnedEmpty = false;
}

export function ensureStatusDictionary() {
  const locale = currentLocale();
  if (loadedLocale === locale && dict.value.size > 0) {
    return Promise.resolve();
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await getStatuses();
      const items = res?.payload?.items || res?.items || [];

      if (!Array.isArray(items) || items.length === 0) {
        if (!warnedEmpty) {
          console.warn(
            "[statusDictionary] /template-action-rules/statuses вернул пустой список"
          );
          warnedEmpty = true;
        }
        return;
      }

      const next = new Map();
      for (const item of items) {
        if (!item?.code) continue;
        next.set(item.code, {
          code: item.code,
          label: item.label || item.code,
          color: safeHex(item.color) || null,
        });
      }
      dict.value = next;
      loadedLocale = locale;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}
